import './DeclarationPeriods.css';

import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {useDebounce} from 'use-debounce';


import {DeclarationPeriodEventsTable} from "../components/declaration-periods-table/DeclarationPeriodEventsTable";
import {TableRef} from "../components/base_table";
import { getDeclarationPeriodTableColumns } from "../components/declaration-periods-table/DeclarationPeriodsTableColumns";
import type { DeclarationPeriodTableColumn } from "../components/declaration-periods-table/DeclarationPeriodsTableColumns";
import {
    mapStatusesToFilterOptions,
    StatusFilterOption,
} from "../components/filing_packs_table/FilingPacksTableColumns";
import {DeclarationPeriod, DeclarationPeriodEvent, DeclarationPeriodFilter} from "../shared/Types";
import {
    ColumnFiltersState,
    ColumnOrderState,
    ExpandedState,
    PaginationState,
    RowSelectionState,
    SortingState
} from "@tanstack/react-table";
import {
    getDeclarationPeriodEventsTableColumns
} from "../components/declaration-periods-table/DeclarationPeriodEventsTableColumns";
import {getEnv} from "../shared/config/OktaConfig";
import {AWS_REGION} from "../shared/utils";
import {overviewService} from "../shared/services/overviewServiceInstance";

type DeclarationPeriodPageState = {
    loading: boolean;
    error: any;
    data: DeclarationPeriod[];
    sorting: SortingState;
    columnOrder: ColumnOrderState;
    columnFilters: ColumnFiltersState;
    rowSelection: RowSelectionState;
    expanded: ExpandedState;
    pagination: PaginationState & { totalCount?: number };
    globalSearch?: string;
    columnFiltersSidePanelIsOpen: boolean;
    viewingDeclarationPeriod?: DeclarationPeriod;
    eventsLoading: boolean;
    eventsError: any;
};

type DeclarationPeriodPageAction =
    | { type: "FETCH_START" }
    | { type: "FETCH_SUCCESS"; payload: { items: DeclarationPeriod[]; totalCount?: number } }
    | { type: "FETCH_ERROR"; payload: any }
    | { type: "SET_SORTING"; payload: SortingState }
    | { type: "SET_COLUMN_ORDER"; payload: ColumnOrderState }
    | { type: "SET_COLUMN_FILTERS"; payload: ColumnFiltersState }
    | { type: "SET_ROW_SELECTION"; payload: RowSelectionState }
    | { type: "SET_VIEWING_DECLARATION_PERIOD"; payload: DeclarationPeriod | undefined }
    | { type: "SET_PAGINATION"; payload: PaginationState }
    | { type: "SET_EXPANDED"; payload: ExpandedState }
    | { type: "SET_GLOBAL_FILTER"; payload: string }
    | { type: "SET_COLUMN_FILTERS_SIDE_PANEL"; payload: boolean }
    | { type: "EVENTS_FETCH_START" }
    | { type: "EVENTS_FETCH_SUCCESS"; payload: { periodId: string; events: DeclarationPeriodEvent[] } }
    | { type: "EVENTS_FETCH_ERROR"; payload: any };

export function declarationPeriodReducer(state: DeclarationPeriodPageState, action: DeclarationPeriodPageAction): DeclarationPeriodPageState {
    switch (action.type) {
        case "FETCH_START":
            return { ...state, loading: true, error: null };
        case "FETCH_SUCCESS":
            return {
                ...state,
                loading: false,
                error: null,
                data: action.payload.items,
                pagination: { ...state.pagination, totalCount: action.payload.totalCount },
            };
        case "FETCH_ERROR":
            return { ...state, loading: false, error: action.payload };
        case "SET_SORTING":
            return { ...state, sorting: action.payload };
        case "SET_COLUMN_ORDER":
            return { ...state, columnOrder: action.payload };
        case "SET_COLUMN_FILTERS":
            return {
                ...state,
                columnFilters: [...action.payload],
                pagination: { ...state.pagination, pageIndex: 0 },
            };
        case "SET_ROW_SELECTION":
            return { ...state, rowSelection: action.payload };
        case "SET_VIEWING_DECLARATION_PERIOD":
            return { ...state, viewingDeclarationPeriod: action.payload };
        case "SET_PAGINATION":
            return { ...state, pagination: { ...state.pagination, ...action.payload } };
        case "SET_GLOBAL_FILTER":
            return { ...state, globalSearch: action.payload };
        case "SET_EXPANDED":
            return { ...state, expanded: action.payload };
        case "SET_COLUMN_FILTERS_SIDE_PANEL":
            return { ...state, columnFiltersSidePanelIsOpen: action.payload };
        case "EVENTS_FETCH_START":
            return { ...state, eventsLoading: true, eventsError: null };
        case "EVENTS_FETCH_SUCCESS":
            return {
                ...state,
                eventsLoading: false,
                eventsError: null,
                data: state.data.map(period =>
                    period.id === action.payload.periodId
                        ? { ...period, events: action.payload.events }
                        : period
                ),
            };
        case "EVENTS_FETCH_ERROR":
            return { ...state, eventsLoading: false, eventsError: action.payload };
        default:
            return state;
    }
}


export function buildQueryFilter(
    columnFilters: ColumnFiltersState,
    columns: DeclarationPeriodTableColumn<DeclarationPeriod>[],
    globalSearch?: string,
): DeclarationPeriodFilter | undefined {
    const filter: DeclarationPeriodFilter = {};

    columnFilters.forEach(({ id, value }) => {
        const col = columns.find(c => c.accessorKey === id);
        const filterKey = col?.filterKey || id;

        if (typeof value === 'string') {
            if (filterKey === 'status') {
                filter.status = value;
            } else if (filterKey === 'declarationPeriod') {
                filter.declarationPeriod = value.replace('-', '');
            } else if (filterKey === 'declarationId') {
                filter.declarationId = value;
            } else if (filterKey === 'lrn') {
                filter.lrn = value;
            } else if (filterKey === 'mrn') {
                filter.mrn = value;
            }
        }
    });

    const hasValidFilter = Object.values(filter).some(
        v => v !== undefined && v !== null && v !== '',
    );

    return hasValidFilter ? filter : undefined;
}

export function useDeclarationPeriodsTable() {
    const tableRef = useRef<TableRef>(null);
    const [statusFilterOptions, setStatusFilterOptions] = useState<StatusFilterOption[]>([]);

    useEffect(() => {
        let cancelled = false;

        async function fetchStatuses() {
            try {
                const result = await overviewService.listDeclarationPeriodStatuses();
                const statuses = (result.data.listDeclarationPeriodStatuses || [])
                    .filter((status: string | null): status is string => !!status);
                if (!cancelled) {
                    setStatusFilterOptions(mapStatusesToFilterOptions(statuses));
                }
            } catch (err) {
                console.error('Failed to load declaration period statuses:', err);
            }
        }

        fetchStatuses();
        return () => { cancelled = true; };
    }, []);

    const columns = useMemo(
        () => getDeclarationPeriodTableColumns(statusFilterOptions),
        [statusFilterOptions],
    );

    const [state, dispatch] = useReducer(declarationPeriodReducer, undefined, () => ({
        loading: false,
        error: null,
        data: [] as DeclarationPeriod[],
        sorting: [{ id: 'declarationPeriod', desc: true }] as SortingState,
        columnOrder: getDeclarationPeriodTableColumns().map(col => col.accessorKey as string),
        columnFilters: [] as ColumnFiltersState,
        rowSelection: {} as RowSelectionState,
        pagination: { pageIndex: 0, pageSize: 10, totalCount: undefined },
        globalSearch: '',
        expanded: {} as ExpandedState,
        columnFiltersSidePanelIsOpen: false,
        viewingDeclarationPeriod: undefined,
        eventsLoading: false,
        eventsError: null,
    }));

    const queryFilter = useMemo(
        () => buildQueryFilter(state.columnFilters, columns, state.globalSearch),
        [state.columnFilters, columns, state.globalSearch],
    );

    const [debouncedFilter] = useDebounce(queryFilter, 1000);

    const { pageIndex, pageSize } = state.pagination;

    useEffect(() => {
        let cancelled = false;

        async function fetchData() {
            dispatch({ type: "FETCH_START" });
            try {
                const filter: DeclarationPeriodFilter = {...debouncedFilter, offset: pageIndex * pageSize, limit: pageSize,};
                const result = await overviewService.listDeclarationPeriods(filter);
                const connection = result.data.listDeclarationPeriods;
                const items = (connection?.items || [])
                    .filter((item: any): item is DeclarationPeriod => !!item?.id);
                if (!cancelled) {
                    dispatch({
                        type: "FETCH_SUCCESS",
                        payload: { items, totalCount: connection?.totalCount ?? items.length },
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch({ type: "FETCH_ERROR", payload: error });
                }
            }
        }

        fetchData();

        return () => { cancelled = true; };
    }, [debouncedFilter, pageIndex, pageSize]);

    const sortedData = useMemo(() => {
        if (!state.sorting.length) return state.data;
        const { id, desc } = state.sorting[0];
        return [...state.data].sort((a, b) => {
            const aVal = (a as any)[id] ?? '';
            const bVal = (b as any)[id] ?? '';
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return desc ? -cmp : cmp;
        });
    }, [state.data, state.sorting]);

    const eventsTableRef = useRef<TableRef>(null);

    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [payloadContent, setPayloadContent] = useState<string | null>(null);
    const [payloadLoading, setPayloadLoading] = useState(false);

    const dismissDownloadError = useCallback(() => setDownloadError(null), []);
    const dismissPayloadContent = useCallback(() => setPayloadContent(null), []);

    const handleDownload = useCallback(async (s3Reference: string) => {
        try {
            setDownloadError(null);
            const region = AWS_REGION;
            const bucket = getEnv('REACT_APP_S3_PAYLOAD_BUCKET') ?? '';
            const result = await overviewService.getPayloadUrl(region, bucket, s3Reference, '');
            const preSignedUrl = result.data.getPayloadUrlOnly?.preSignedUrl;
            if (preSignedUrl) {
                const response = await fetch(preSignedUrl);
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.setAttribute("download", s3Reference);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
        } catch (err: any) {
            const message = err?.message || String(err);
            console.error("Failed to download payload:", err);

            setDownloadError(`Failed to download payload: ${message}`);
        }
    }, []);

    const handleShowPayload = useCallback(async (s3Reference: string) => {
        try {
            setDownloadError(null);
            setPayloadLoading(true);
            const region = AWS_REGION;
            const bucket = getEnv('REACT_APP_S3_PAYLOAD_BUCKET') ?? '';
            const result = await overviewService.getPayloadUrl(region, bucket, s3Reference, '');
            const preSignedUrl = result.data.getPayloadUrlOnly?.preSignedUrl;
            if (preSignedUrl) {
                const response = await fetch(preSignedUrl);
                const text = await response.text();
                try {
                    const json = JSON.parse(text);
                    setPayloadContent(JSON.stringify(json, null, 2));
                } catch {
                    setPayloadContent(text);
                }
            }
        } catch (err: any) {
            const message = err?.message || String(err);
            console.error("Failed to download payload:", err);

            if (message.includes('PayloadTooLargeException')) {
                setDownloadError('The payload is too large for viewing. Please download it instead.');
            } else {
                setDownloadError(`Failed to fetch payload: ${message}`);
            }
        } finally {
            setPayloadLoading(false);
        }
    }, []);

    const eventColumns = useCallback(() => getDeclarationPeriodEventsTableColumns(handleDownload, handleShowPayload), [handleDownload, handleShowPayload]);

    const renderExpandedContent = useCallback((declarationPeriod: DeclarationPeriod) => {
        return (
            <DeclarationPeriodEventsTable
                tableRef={eventsTableRef}
                columns={eventColumns}
                data={declarationPeriod.events || []}
                isLoading={state.eventsLoading}
                error={state.eventsError}
            />
        );
    }, [eventColumns, state.eventsLoading, state.eventsError]);

    const onSortChange = useCallback((newSorting: SortingState) =>
        dispatch({ type: "SET_SORTING", payload: newSorting }), []);

    // Clear events when Search is triggered
    const onColumnFiltersChange = useCallback((newFilters: ColumnFiltersState) => {
        dispatch({ type: "SET_COLUMN_FILTERS", payload: newFilters });
        dispatch({ type: "SET_EXPANDED", payload: {} });
        dispatch({ type: "SET_ROW_SELECTION", payload: {} });
        dispatch({ type: "SET_VIEWING_DECLARATION_PERIOD", payload: undefined });
    }, []);

    const onColumnOrderChange = useCallback((newOrder: ColumnOrderState) =>
        dispatch({ type: "SET_COLUMN_ORDER", payload: newOrder }), []);

    const onRowSelectionChange = useCallback((newRowSelection: RowSelectionState) => {
        const rowId = Object.keys(newRowSelection)[0];
        const isSelected = !!state.rowSelection[rowId];
        const isExpanded = typeof state.expanded === 'object' && !!state.expanded?.[rowId];

        if (isSelected && isExpanded) {
            dispatch({ type: "SET_ROW_SELECTION", payload: {} });
            dispatch({ type: "SET_VIEWING_DECLARATION_PERIOD", payload: undefined });
            dispatch({ type: "SET_EXPANDED", payload: {} });
        } else {
            const selectedPeriod = state.data.find((fp) => fp.id === rowId);
            dispatch({ type: "SET_ROW_SELECTION", payload: { [rowId]: true } });
            dispatch({ type: "SET_VIEWING_DECLARATION_PERIOD", payload: selectedPeriod });
            dispatch({ type: "SET_EXPANDED", payload: { [rowId]: true } });

            if (selectedPeriod) {
                dispatch({ type: "EVENTS_FETCH_START" });
                overviewService.listDeclarationPeriodEvents({ periodId: selectedPeriod.id })
                    .then(result => {
                        const events = result.data.listDeclarationPeriodEvents || [];
                        dispatch({ type: "EVENTS_FETCH_SUCCESS", payload: { periodId: rowId, events } });
                    })
                    .catch(error => {
                        dispatch({ type: "EVENTS_FETCH_ERROR", payload: error });
                    });
            }
        }
    }, [state.rowSelection, state.expanded, state.data]);

    const onPaginationChange = useCallback((pagination: PaginationState) =>
        dispatch({ type: "SET_PAGINATION", payload: pagination }), []);

    // Clear events when Search is triggered
    const onGlobalFilterChange = useCallback((filter: string) => {
        dispatch({ type: "SET_GLOBAL_FILTER", payload: filter });
        dispatch({ type: "SET_EXPANDED", payload: {} });
        dispatch({ type: "SET_ROW_SELECTION", payload: {} });
        dispatch({ type: "SET_VIEWING_DECLARATION_PERIOD", payload: undefined });
    }, []);

    const onExpandedChange = useCallback((expanded: ExpandedState) =>
        dispatch({ type: "SET_EXPANDED", payload: expanded }), []);

    const onColumnFiltersSidePanelOpenChange = useCallback((isOpen: boolean) =>
        dispatch({ type: "SET_COLUMN_FILTERS_SIDE_PANEL", payload: isOpen }), []);

    return {
        tableRef,
        columns,
        data: state.loading ? [] : sortedData,
        isLoading: state.loading,
        error: state.error,
        downloadError,
        dismissDownloadError,
        payloadContent,
        payloadLoading,
        dismissPayloadContent,
        renderExpandedContent,

        sorting: state.sorting,
        columnFilters: state.columnFilters,
        columnOrder: state.columnOrder,
        rowSelection: state.rowSelection,
        expanded: state.expanded,
        globalSearch: state.globalSearch,
        columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen,
        pageIndex: state.pagination.pageIndex,
        pageSize: state.pagination.pageSize,
        totalCount: state.pagination.totalCount,

        onSortChange,
        onColumnFiltersChange,
        onColumnOrderChange,
        onRowSelectionChange,
        onPaginationChange,
        onGlobalFilterChange,
        onExpandedChange,
        onColumnFiltersSidePanelOpenChange,
    };
}

